/**
 * Workflow engagement panel (Session 61).
 *
 * Replaces the original 4-tile flat read-out with:
 *   • full funnel stats — sent / delivered / opens (raw + filtered) /
 *     clicks / replies / bounces / unsubscribes
 *   • a horizontal bar chart that shows the same numbers visually so
 *     "lots of opens, few clicks, zero replies" is obvious at a glance
 *   • clickable tiles that pop a drawer listing exactly WHO triggered
 *     the event (name, email, company, when, variant, prefetcher flag)
 *   • a "Real opens" tile that strips out Gmail Image Proxy / Apple
 *     Mail Privacy Protection / Outlook protected-view prefetchers,
 *     because cold-outbound headline opens are typically ~60% inflated
 *     by automatic image-prefetching.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell,
  Tooltip as ReTooltip, LabelList,
} from "recharts";
import { X, ArrowSquareOut } from "@phosphor-icons/react";
import { formatLocal } from "@/lib/timezone";

const TILE_COLORS = {
  sent:         "#525252",
  delivered:    "#0EA5E9",
  opens:        "#16A34A",
  opens_real:   "#15803D",
  clicks:       "#7C3AED",
  replies:      "#F59E0B",
  bounces:      "#DC2626",
  unsubs:       "#A3A3A3",
};

function pct(num, denom) {
  if (!denom) return "0.0%";
  return `${((100 * num) / denom).toFixed(1)}%`;
}

function Tile({ label, value, sublabel, color, onClick, testId }) {
  const interactive = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive || !value}
      data-testid={testId}
      className={
        "px-4 py-3 bg-white text-left transition-all duration-150 " +
        (interactive && value
          ? "hover:bg-[#FAFAFA] hover:shadow-sm cursor-pointer"
          : "cursor-default")
      }
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3] flex items-center gap-1.5">
        <span className="inline-block w-1.5 h-1.5" style={{ background: color }} />
        {label}
      </div>
      <div className="text-2xl font-light tabular-nums mt-0.5" style={{ color }}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      {sublabel && (
        <div className="text-[10px] text-[#A3A3A3] tabular-nums mt-0.5">{sublabel}</div>
      )}
    </button>
  );
}

function FunnelChart({ totals, onBarClick }) {
  // Each bar carries the event_type + display label so a click can pop
  // the same drill-down drawer the tiles use.
  const data = [
    { name: "Sent",        value: totals.sent || 0,                color: TILE_COLORS.sent,        eventType: "sent",          label: "Sent" },
    { name: "Delivered",   value: totals.delivered || 0,           color: TILE_COLORS.delivered,   eventType: "delivered",     label: "Delivered" },
    { name: "Opens (raw)", value: totals.unique_opens || 0,        color: TILE_COLORS.opens,       eventType: "opened",        label: "Opens (raw)" },
    { name: "Real opens",  value: totals.unique_opens_real || 0,   color: TILE_COLORS.opens_real,  eventType: "opened_real",   label: "Real opens (humans)" },
    { name: "Clicks",      value: totals.unique_clicks || 0,       color: TILE_COLORS.clicks,      eventType: "clicked",       label: "Clicks" },
    { name: "Replies",     value: totals.unique_replies || 0,      color: TILE_COLORS.replies,     eventType: "replied",       label: "Replies" },
    { name: "Bounces",     value: totals.unique_bounces || 0,      color: TILE_COLORS.bounces,     eventType: "bounced",       label: "Bounces" },
    { name: "Unsubs",      value: totals.unique_unsubscribes || 0, color: TILE_COLORS.unsubs,      eventType: "unsubscribed",  label: "Unsubscribes" },
  ];
  // Recharts hands us the data row as the first argument when a bar is
  // clicked. Guard against label/empty-bar clicks.
  const handleBarClick = (entry) => {
    if (!entry || !entry.value) return;
    onBarClick && onBarClick(entry.eventType, entry.label);
  };
  // Y-axis ticks are SVG <text> nodes — wire them up to the same
  // handler so clicking the metric NAME also drills in (matches the
  // user's "click the colored line" mental model).
  const renderTick = ({ x, y, payload }) => {
    const row = data.find(d => d.name === payload.value);
    const clickable = row && row.value > 0;
    return (
      <text
        x={x} y={y} dy={3} textAnchor="end"
        style={{
          fontSize: 10,
          fill: clickable ? "#0055FF" : "#A3A3A3",
          cursor: clickable ? "pointer" : "default",
          fontWeight: clickable ? 600 : 400,
          textDecoration: clickable ? "underline" : "none",
        }}
        onClick={() => row && handleBarClick(row)}
        data-testid={`funnel-label-${row?.eventType || "empty"}`}
      >
        {payload.value}
      </text>
    );
  };
  return (
    <div className="bg-white border border-[#E5E5E5] mt-3" data-testid="wf-engagement-chart">
      <div className="px-4 py-2.5 border-b border-[#E5E5E5] flex items-center justify-between">
        <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252]">
          Funnel · Visual
        </h3>
        <span className="text-[9px] text-[#A3A3A3]">
          Click any bar or label to see who. Real opens strips automated image-proxy fetches.
        </span>
      </div>
      <div className="p-3" style={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
            <XAxis type="number" tick={{ fontSize: 10, fill: "#A3A3A3" }} />
            <YAxis dataKey="name" type="category" width={92}
                   interval={0} tick={renderTick} />
            <ReTooltip
              cursor={{ fill: "#F5F5F5" }}
              contentStyle={{ fontSize: 11, border: "1px solid #E5E5E5", padding: "4px 8px" }}
              formatter={(value) => [value, "Click bar to drill down"]}
            />
            <Bar dataKey="value" radius={[0, 0, 0, 0]} onClick={handleBarClick}
                 style={{ cursor: "pointer" }} data-testid="funnel-bar">
              {data.map((d, i) => (
                <Cell key={i} fill={d.color}
                      style={{ cursor: d.value ? "pointer" : "default" }}
                      data-testid={`funnel-bar-cell-${d.eventType}`} />
              ))}
              <LabelList dataKey="value" position="right" style={{ fontSize: 10, fill: "#525252" }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ClickHeatMap({ data }) {
  // Compact per-URL bar chart rendered above the clicks drill-down
  // table. Answers "which call-to-action is earning traction" in one
  // glance — bar widths represent % of total clicks, the second small
  // bar in grey shows the unique-contact share (breadth). Colour goes
  // from the Pulse click-purple (heavy traffic) to a muted violet (low
  // traffic) so the winner is visually obvious.
  const [expanded, setExpanded] = useState(false);
  const rows = expanded ? data.urls : data.urls.slice(0, 5);
  const max = Math.max(...data.urls.map(u => u.pct_of_clicks), 1);
  const friendly = (u) => {
    try {
      const url = new URL(u);
      const path = url.pathname === "/" ? "" : url.pathname;
      return `${url.hostname}${path}`;
    } catch {
      return u;
    }
  };
  return (
    <div className="px-5 py-3 bg-[#FAFAFA] border-b border-[#E5E5E5]"
         data-testid="engagement-drawer-click-heatmap">
      <div className="flex items-baseline justify-between mb-2">
        <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252]">
          Per-URL heat-map
        </h4>
        <span className="text-[10px] text-[#A3A3A3] tabular-nums">
          {data.total_clicks} clicks · {data.unique_clickers} unique clickers · {data.urls.length} distinct URLs
        </span>
      </div>
      <div className="space-y-1.5" data-testid="click-heatmap-rows">
        {rows.map((u, i) => {
          const widthPct = Math.max(2, (u.pct_of_clicks / max) * 100);
          // Pulse-purple, fading to muted as rank drops.
          const intensity = Math.max(0.35, 1 - (i * 0.12));
          return (
            <div key={u.url} className="group" data-testid={`click-heatmap-row-${i}`}>
              <div className="flex items-center gap-2 text-[11px]">
                <span className="w-[48px] text-right font-bold tabular-nums text-[#171717]">
                  {u.pct_of_clicks.toFixed(1)}%
                </span>
                <div className="flex-1 relative h-5 bg-[#F5F5F5]">
                  <div
                    className="absolute inset-y-0 left-0 transition-all duration-300"
                    style={{
                      width: `${widthPct}%`,
                      background: `rgba(124, 58, 237, ${intensity})`,
                    }}
                  />
                  <div className="absolute inset-0 flex items-center px-2">
                    <a
                      href={u.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-[#171717] hover:underline truncate max-w-full"
                      title={u.url}
                    >
                      {friendly(u.url)}
                    </a>
                  </div>
                </div>
                <span className="w-[110px] text-right text-[10px] text-[#525252] tabular-nums">
                  {u.clicks} clicks · {u.unique_contacts} ppl
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {data.urls.length > 5 && (
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="mt-2 text-[10px] text-[#7C3AED] font-bold uppercase tracking-wider hover:underline"
          data-testid="click-heatmap-toggle"
        >
          {expanded ? "Show top 5" : `Show all ${data.urls.length}`}
        </button>
      )}
    </div>
  );
}

function ContactsDrawer({ workflowId, eventType, label, onClose }) {
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [clickMap, setClickMap] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const tasks = [
      api.get(`/marketing/workflows/${workflowId}/event-contacts`, {
        params: { event_type: eventType, limit: 1000 },
      }).then(r => { if (!cancelled) setContacts(r.data?.contacts || []); }),
    ];
    // For clicks, also fetch the per-URL heat-map so the operator can
    // see "60% of clicks went to /pricing" without scrolling the table.
    if (eventType === "clicked") {
      tasks.push(
        api.get(`/marketing/workflows/${workflowId}/click-map`)
          .then(r => { if (!cancelled) setClickMap(r.data); })
          .catch(() => {}),   // heat-map is a non-critical enhancement
      );
    }
    Promise.all(tasks)
      .catch(e => {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || e?.message || "Failed to load";
        setError(msg);
        toast.error(`Could not load list: ${msg}`);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workflowId, eventType]);

  const realCount = contacts.filter(c => !c.prefetched).length;
  const hasPrefetchHint = eventType === "opened" && realCount < contacts.length;
  const isClicked = eventType === "clicked";
  const isReplied = eventType === "replied";

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/30"
      onClick={onClose}
      data-testid="engagement-drawer"
    >
      <div
        className="bg-white w-full max-w-3xl h-full flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
              Drill-down
            </div>
            <div className="text-base font-bold text-[#171717]">
              {label} <span className="text-[#A3A3A3] text-sm">({contacts.length})</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-[#F5F5F5]"
            aria-label="Close"
            data-testid="engagement-drawer-close"
          >
            <X size={16} />
          </button>
        </div>

        {hasPrefetchHint && (
          <div className="px-5 py-2 bg-[#FEF3C7] border-b border-[#F59E0B] text-[10px] text-[#92400E]"
               data-testid="engagement-drawer-prefetch-warning">
            <strong>{contacts.length - realCount} of {contacts.length}</strong> are likely
            automated image-prefetcher events — fired on delivery, not on actual open.{" "}
            <strong>Real human opens: {realCount}.</strong>
          </div>
        )}

        {isReplied && contacts.length > 0 && (
          <div className="px-5 py-2 bg-[#EFF6FF] border-b border-[#3B82F6] text-[11px] text-[#1E3A8A] flex items-center justify-between"
               data-testid="engagement-drawer-replies-link">
            <span>Replies are stored against the contact's CRM lead with full body & threading.</span>
            <button
              type="button"
              onClick={() => navigate("/crm/replies")}
              className="ml-3 px-2 py-1 bg-[#1E3A8A] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#1E40AF] flex items-center gap-1"
              data-testid="engagement-drawer-open-replies-inbox"
            >
              Open Replies Inbox <ArrowSquareOut size={11} weight="bold" />
            </button>
          </div>
        )}

        {isClicked && clickMap && clickMap.urls?.length > 0 && (
          <ClickHeatMap data={clickMap} />
        )}

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="p-6 text-sm text-[#A3A3A3] text-center">Loading…</div>
          )}
          {error && !loading && (
            <div className="p-6 text-sm text-[#DC2626] text-center">{error}</div>
          )}
          {!loading && !error && contacts.length === 0 && (
            <div className="p-6 text-sm text-[#A3A3A3] text-center">
              No contacts to show for this event yet.
            </div>
          )}
          {!loading && contacts.length > 0 && (
            <table className="w-full text-xs" data-testid="engagement-drawer-table">
              <thead className="bg-[#FAFAFA] text-[9px] uppercase tracking-wider text-[#A3A3A3] sticky top-0">
                <tr>
                  <th className="text-left px-4 py-2 font-bold">Contact</th>
                  <th className="text-left px-3 py-2 font-bold">Company</th>
                  {isClicked && <th className="text-left px-3 py-2 font-bold">URL clicked</th>}
                  <th className="text-left px-3 py-2 font-bold">Device · Client</th>
                  <th className="text-center px-2 py-2 font-bold">Var</th>
                  <th className="text-right px-4 py-2 font-bold">When</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c, i) => (
                  <tr key={`${c.send_id}-${i}-${c.url || ""}`} className="border-t border-[#F5F5F5]">
                    <td className="px-4 py-2">
                      <div className="font-medium text-[#171717]">{c.name || c.email}</div>
                      <div className="text-[#A3A3A3] text-[10px]">{c.email}</div>
                    </td>
                    <td className="px-3 py-2 text-[#525252] max-w-[140px] truncate">{c.company || "—"}</td>
                    {isClicked && (
                      <td className="px-3 py-2 max-w-[260px]">
                        {c.url ? (
                          <a href={c.url} target="_blank" rel="noopener noreferrer"
                             className="text-[#7C3AED] hover:underline text-[11px] break-all"
                             data-testid={`engagement-drawer-url-${i}`}>
                            {c.url.length > 50 ? c.url.slice(0, 50) + "…" : c.url}
                          </a>
                        ) : (
                          <span className="text-[10px] text-[#A3A3A3]">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2 text-[10px]">
                      {c.client === "Unknown" ? (
                        <span className="text-[#A3A3A3]">—</span>
                      ) : (
                        <>
                          <div className="text-[#525252]">
                            <span className="text-[#A3A3A3]">{c.device || "?"}</span>
                            {" · "}
                            <span className={c.type === "bot" ? "text-[#92400E] font-bold" : "text-[#171717] font-medium"}>
                              {c.client}
                            </span>
                          </div>
                          <div className="text-[9px]">
                            {c.type === "bot" ? (
                              <span className="text-[#92400E]">⚠ Auto image-fetch (not a real open)</span>
                            ) : (
                              <span className="text-[#16A34A]">Human</span>
                            )}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {c.variant_id ? (
                        <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#F5F3FF] text-[#7C3AED] border border-[#E9D5FF]">
                          {c.variant_id}
                        </span>
                      ) : (
                        <span className="text-[9px] text-[#A3A3A3]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right text-[#A3A3A3] text-[10px] tabular-nums whitespace-nowrap">
                      {c.occurred_at ? formatLocal(c.occurred_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EngagementPanel({ engagement, workflowId }) {
  const [drillEvent, setDrillEvent] = useState(null); // {event_type, label}

  if (!engagement) return null;
  const t = engagement.totals || {};

  const open = (eventType, label) => () => setDrillEvent({ event_type: eventType, label });

  return (
    <>
      <div className="mb-5" data-testid="wf-engagement-panel">
        <div className="bg-white border border-[#E5E5E5]">
          <div className="px-4 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
              Engagement
            </h2>
            <span className="text-[10px] text-[#A3A3A3]">
              All time · click any tile to see who
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-px bg-[#F5F5F5]">
            <Tile label="Sent" value={t.sent || 0} color={TILE_COLORS.sent}
                  onClick={open("sent", "Sent")} testId="eng-tile-sent" />
            <Tile label="Delivered" value={t.delivered || 0} color={TILE_COLORS.delivered}
                  sublabel={pct(t.delivered, t.sent)}
                  onClick={open("delivered", "Delivered")} testId="eng-tile-delivered" />
            <Tile label="Opens (raw)" value={t.unique_opens || 0} color={TILE_COLORS.opens}
                  sublabel={pct(t.unique_opens, t.sent) + " open rate"}
                  onClick={open("opened", "Opens (raw)")} testId="eng-tile-opens" />
            <Tile label="Real opens" value={t.unique_opens_real || 0} color={TILE_COLORS.opens_real}
                  sublabel={pct(t.unique_opens_real, t.sent) + " · bot-stripped"}
                  onClick={open("opened_real", "Real opens (humans)")}
                  testId="eng-tile-opens-real" />
            <Tile label="Clicks" value={t.unique_clicks || 0} color={TILE_COLORS.clicks}
                  sublabel={pct(t.unique_clicks, t.sent) + " CTR"}
                  onClick={open("clicked", "Clicks")} testId="eng-tile-clicks" />
            <Tile label="Replies" value={t.unique_replies || 0} color={TILE_COLORS.replies}
                  sublabel={pct(t.unique_replies, t.sent)}
                  onClick={open("replied", "Replies")} testId="eng-tile-replies" />
            <Tile label="Bounces" value={t.unique_bounces || 0} color={TILE_COLORS.bounces}
                  sublabel={pct(t.unique_bounces, t.sent)}
                  onClick={open("bounced", "Bounces")} testId="eng-tile-bounces" />
            <Tile label="Unsubscribes" value={t.unique_unsubscribes || 0} color={TILE_COLORS.unsubs}
                  sublabel={pct(t.unique_unsubscribes, t.sent)}
                  onClick={open("unsubscribed", "Unsubscribes")} testId="eng-tile-unsubs" />
          </div>
        </div>
        <FunnelChart totals={t} onBarClick={(eventType, label) => setDrillEvent({ event_type: eventType, label })} />
      </div>

      {drillEvent && (
        <ContactsDrawer
          workflowId={workflowId}
          eventType={drillEvent.event_type}
          label={drillEvent.label}
          onClose={() => setDrillEvent(null)}
        />
      )}
    </>
  );
}
